/**
 * The BehaviorCensus: pattern matchers over sim events and stats that fill
 * the OBSERVATIONS logbook. Entries are only ever recorded when they
 * genuinely happened — nothing is scripted.
 */
import { EV, type SimEvent, type WorkerStats } from '../sim/protocol';
import { ITEM } from '../sim/items';
import { unpackUtter } from '../sim/meta/language';
import type { LogEntry } from '../ui/logbook';

interface CensusDef {
  id: string;
  name: string;
  hint: string;
}

const DEFS: CensusDef[] = [
  { id: 'hatch', name: 'FIRST LIGHT', hint: 'it begins with one' },
  { id: 'split', name: 'MITOSIS', hint: 'one becomes two' },
  { id: 'dozen', name: 'A DOZEN STRONG', hint: 'keep them thriving' },
  { id: 'fifty', name: 'HALF A HUNDRED', hint: 'the meadow hums' },
  { id: 'dance', name: 'SONG CIRCLE', hint: 'joy is contagious' },
  { id: 'chorus', name: 'EVENING CHORUS', hint: 'they sing together' },
  { id: 'feeder', name: 'THE ORCHARD', hint: 'they will provide' },
  { id: 'spring', name: 'THE SPRING', hint: 'they like to be clean' },
  { id: 'totem', name: 'THE TOTEM', hint: 'they made a monument' },
  { id: 'dormant', name: 'THE LONG SLEEP', hint: 'nothing is ever lost' },
  { id: 'wake', name: 'REVIVAL', hint: 'a gentle hand returns' },
  { id: 'glyphs', name: 'FIRST WRITING', hint: 'they will find the words' },
  { id: 'speech', name: 'PLAIN SPEECH', hint: 'keep listening' },
];

export class BehaviorCensus {
  private readonly discovered = new Map<string, number>(); // id -> times seen

  constructor(restored: string[] | undefined) {
    for (const id of restored ?? []) this.discovered.set(id, 1);
  }

  /** Returns display names of entries discovered just now. */
  processEvents(events: SimEvent[]): string[] {
    const fresh: string[] = [];
    let chirpsInBatch = 0;
    for (const [kind, , , a, b] of events) {
      switch (kind) {
        case EV.HATCH:
          this.record('hatch', fresh);
          break;
        case EV.MITOSIS:
          this.record('split', fresh);
          break;
        case EV.DANCE_START:
          this.record('dance', fresh);
          break;
        case EV.DORMANT:
          this.record('dormant', fresh);
          break;
        case EV.WAKE:
          this.record('wake', fresh);
          break;
        case EV.BUILT:
          this.record(
            a === ITEM.SPRING ? 'spring' : a === ITEM.TOTEM ? 'totem' : 'feeder',
            fresh,
          );
          break;
        case EV.CHIRP:
          chirpsInBatch++;
          break;
        case EV.UTTER: {
          const { stage } = unpackUtter(b);
          if (stage >= 2) this.record('glyphs', fresh);
          if (stage >= 4) this.record('speech', fresh);
          break;
        }
      }
    }
    if (chirpsInBatch >= 3) this.record('chorus', fresh);
    return fresh;
  }

  processStats(stats: WorkerStats): string[] {
    const fresh: string[] = [];
    if (stats.pop >= 12) this.record('dozen', fresh);
    if (stats.pop >= 50) this.record('fifty', fresh);
    return fresh;
  }

  private record(id: string, fresh: string[]): void {
    const seen = this.discovered.get(id);
    if (seen === undefined) {
      this.discovered.set(id, 1);
      const def = DEFS.find((d) => d.id === id);
      if (def) fresh.push(def.name);
    } else {
      this.discovered.set(id, seen + 1);
    }
  }

  get discoveredIds(): string[] {
    return [...this.discovered.keys()];
  }

  entries(): LogEntry[] {
    return DEFS.map((d) => ({
      id: d.id,
      name: d.name,
      hint: d.hint,
      discovered: this.discovered.has(d.id),
      timesSeen: this.discovered.get(d.id) ?? 0,
    }));
  }
}

